import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { Collapse, Icon } from '@mui/material'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { TranslatableText } from 'src/feature/localization/component/atom/TranslatableText'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { If } from 'src/component/common/original/atom/If'
import { AppUnstyledButton } from 'src/component/common/analog/molecule/button/AppUnstyledButton'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { NavModel } from 'src/model/Nav'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { mergeTranslationWithObjects } from 'src/feature/localization/util/localization.helper'
import { Sidebar } from 'src/feature/sidebar/component/Sidebar'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { useTourSteps } from 'src/hook/useTourSteps'
import { expandedSidebarDropdownSx } from 'src/feature/sidebar/component/sidebarDropdownStyles'

export type ProjectBuilderSidebarProps = {
  isOpen: boolean
}

export const ProjectBuilderSidebar = observer(({ isOpen }: ProjectBuilderSidebarProps) => {
  const { translation } = useLocalizationStore()

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { projectBuilderSidebar } = translation.components
  const { getStepClass } = useTourSteps()

  const navLinks: NavModel<'props.link'> = {
    projectBuilder: {
      props: {
        link: appRoutePath.projectBuilder,
        hidden: false,
      },
    },
    projectBuilderCreate: {
      props: {
        link: appRoutePath.projectBuilderCreate,
        hidden: false,
      },
    },
  }

  const desktopNav = mergeTranslationWithObjects(projectBuilderSidebar.nav, navLinks) as unknown as NavModel

  return (
    <>
      <AppBox
        alignItems='center'
        width='100%'
        sx={{
          cursor: 'pointer',
          justifyContent: {
            xs: 'center',
            md: 'space-between',
          },
        }}
        onClick={() => isOpen && setIsMenuOpen(!isMenuOpen)}
      >
        <AppBox justifyContent='center' alignItems='center' gap={7} className={getStepClass('projectBuilder')}>
          <Icon
            sx={{
              color: 'text.primary',
              transition: 'color 0.3s ease',
              fontSize: 16,
              height: 16,
              width: 16,
            }}
            component={AutoAwesomeIcon}
          />
          {isOpen && <TranslatableText
            component={'p'}
            textValue={projectBuilderSidebar.title()}
            sx={{
              transition: 'color 0.3s ease',
              color: 'text.primary',
              fontSize: '14px',
            }}
          />}
        </AppBox>
        <AppUnstyledButton
          size='small'
          sx={{
            width: 20,
            height: 20,
            minWidth: 20,
            minHeight: 20,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            backgroundColor: isMenuOpen && isOpen ? 'rgba(125, 235, 228, 0.2)' : 'transparent',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: 'rgba(125, 235, 228, 0.12)',
            },
          }}>
          <If condition={isMenuOpen && isOpen}>
            <ExpandMoreIcon sx={{ color: 'text.primary', fontSize: 16, height: 16, width: 16 }} />
          </If>
          <If condition={!isMenuOpen && isOpen}>
            <ChevronRightIcon sx={{ color: 'text.primary', fontSize: 16, height: 16, width: 16 }} />
          </If>
        </AppUnstyledButton>
      </AppBox>
      <Collapse
        in={isMenuOpen && isOpen}
        sx={{
          width: '100%',
          ...expandedSidebarDropdownSx,
          '& .link-label': {
            fontSize: '14px',
          },
        }}
        mountOnEnter
        unmountOnExit
      >
        <Sidebar nav={desktopNav} />
      </Collapse>
    </>
  )
})